// What a coach's reply may carry beyond its words (body.steps, body.sources,
// body.cards; pure, tested without a browser). Each read is safe: an absent
// field or a malformed row is nothing on screen. Mobile's lib/v2/replyExtras.ts
// has the same rules.
//
// Stream C sets the cards' exact shape; until then one reader per kind holds the
// assumption, and a card that doesn't match isn't drawn:
//   session  { id, title, date?, sport?, duration_s?, detail? }
//   activity { id, title, date?, sport?, duration_s?, distance_m?, detail? }
//   workout  { id, title, sport?, duration_s?, detail? }
//   prediction { title, value, detail?, race_id?, rows?: [{ label, value }] }
//   confirm  { proposal_id, title, text?, confirm_label? }: the proposal's own accept

const MAX_SOURCES = 6;
const SOURCE_KINDS = ['activity', 'session', 'race'];
const isText = (v) => typeof v === 'string' && v.trim() !== '';
const hasId = (v) => isText(v) || Number.isFinite(v);

export function readSteps(body) {
  return Array.isArray(body?.steps) ? body.steps.filter(isText) : [];
}

/** Code fills these from what the tools read, so each opens the object it names. */
export function readSources(body) {
  if (!Array.isArray(body?.sources)) return [];
  return body.sources.filter((s) => SOURCE_KINDS.includes(s?.kind) && hasId(s.id) && isText(s.label)).slice(0, MAX_SOURCES);
}

const rowsOf = (raw) => (Array.isArray(raw) ? raw.filter((r) => isText(r?.label) && isText(r?.value)) : []);
const identified = (c) => (hasId(c.id) && isText(c.title) ? c : null);

const READERS = {
  session: identified,
  activity: identified,
  workout: identified,
  prediction: (c) => (isText(c.title) && isText(c.value) ? { ...c, rows: rowsOf(c.rows) } : null),
  confirm: (c) => (hasId(c.proposal_id) && isText(c.title) ? c : null),
};

export function readCards(body) {
  if (!Array.isArray(body?.cards)) return [];
  return body.cards.map((c) => READERS[c?.kind]?.(c) || null).filter(Boolean);
}

export const hasExtras = (body) => Boolean(readSteps(body).length || readSources(body).length || readCards(body).length);

/** A session's or an activity's one line of facts: "Sun 2 Nov · 1:45 · 52.3 km". */
export function factsLine({ date, duration_s: seconds, distance_m: metres }) {
  const parts = [];
  if (isText(date)) {
    const day = new Date(`${date.slice(0, 10)}T12:00:00`);
    if (!Number.isNaN(day.getTime())) parts.push(day.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }));
  }
  if (Number.isFinite(seconds) && seconds > 0) {
    const minutes = Math.round(seconds / 60);         // 59 min 40 s is 1:00, not 0:00
    parts.push(`${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`);
  }
  if (Number.isFinite(metres) && metres > 0) parts.push(`${(metres / 1000).toFixed(1)} km`);
  return parts.join(' · ');
}

/** A confirm card's state, from its proposal in the thread: open until it says otherwise. */
export function confirmView(card, { thread = {}, pending = [] } = {}) {
  const id = String(card.proposal_id);
  const state = thread[id]?.state || 'active';
  const sending = pending.some((c) => c.op?.kind === 'item_state' && String(c.op.id) === id);
  return { open: state === 'active' && !sending, sending, done: state === 'accepted' ? 'Done' : state === 'active' ? null : 'Not now' };
}
